import React, { useEffect, useState, useRef } from "react";
import { PropTypes } from "prop-types";
import { useNavigate } from "react-router-dom";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import AppHOC from "../AppHOC";
import ManagerTab from "./ManagerTab";
import Menu from "./Menu";
import MultiTenantMenu from "./MultiTenantMenu";
import { useMount } from "../../utils/genericCommon";
import {
  getConvergedCookie,
  getLocalStorageFlag,
  setLocalStorageFlag
} from "../../utils/common";
import { getMenuItems } from "../../utils/license";
import { getCookie } from "../../serviceWorker";
import apiService from "../../config/api-config";
import { useI18n } from "../../i18n/useI18n";
import css from "../genericMagneticStyle.less";
import { routeURLs } from "../../utils/enums";

const [
  HbrMenuDivider,
  HbrShellNav,
  HbrNavItem,
  HbrIcon,
  HbrMenu,
  HbrMenuItem
] = reactWrapper([
  "hbr-menu-divider",
  "hbr-shell-nav",
  "hbr-nav-item",
  "hbr-icon",
  "hbr-menu",
  "hbr-menu-item"
]);

const SYSTEM360_MENU_IDS = {
  tools: "sidebar_menu_tools",
  system360: "sidebar_submenu_system360"
};

const normalizeUserValue = value =>
  value ? value.toString().replace(/^['"]|['"]$/g, "").trim().toLowerCase() : "";

const isTrueResponse = value =>
  value === true || normalizeUserValue(value) === "true";

const hasSystem360Menu = navList =>
  Array.isArray(navList) && navList.some(item =>
    item.id === SYSTEM360_MENU_IDS.tools &&
    Array.isArray(item.submenu) &&
    item.submenu.some(submenu => submenu.id === SYSTEM360_MENU_IDS.system360)
  );

const removeSystem360Menu = navList =>
  navList.map(item => {
    if (item.id !== SYSTEM360_MENU_IDS.tools || !Array.isArray(item.submenu)) {
      return item;
    }

    return {
      ...item,
      submenu: item.submenu.filter(
        submenu => submenu.id !== SYSTEM360_MENU_IDS.system360
      )
    };
  });

const MegaMenu = ({ globalFilter, setMenuState, setAltoProps }) => {
  const { tGenerics, i18n } = useI18n('generics');
  const navigate = useNavigate();
  const [menuItems, setMenuItems] = useState([]);
  const [vManageNavList, setVManageNavList] = useState([]);
  const mount = useMount();
  const isUIconvergence = getCookie("cl-analytics");
  const vManageURI = getCookie("cl-vManageURI");
  const tenantId = getConvergedCookie("tenantId");
  const menuTypeConvergence = useRef(false);
  const mode = useRef(getConvergedCookie("mode"));

  const filterSystem360Menu = async navList => {
    if (!hasSystem360Menu(navList)) return navList;

    const response = await apiService.getDiagnosticEnabled();

    if (response?.errorObject instanceof Object) {
      return removeSystem360Menu(navList);
    }

    return isTrueResponse(response?.data) ? navList : removeSystem360Menu(navList);
  };

  const updateMenuLabels = (menuList, i18nResponse) => {
    // Modify label with i18n file the menuList in place
    menuList.forEach(element => {
      element.text = i18nResponse[element.i18nKey] ? i18nResponse[element.i18nKey] : element.text;
      if (element.submenu) {
        element.submenu.forEach(subElement => {
          subElement.text = i18nResponse[subElement.i18nKey] ? i18nResponse[subElement.i18nKey] : subElement.text;
          if (i18nResponse[subElement.i18nKey] === "Console Line Access") {
            subElement.sref = subElement.sref.replace("nanook", "console_line_access");
          }
          //nested submenu
          //i.e Logs -> Traffic Logs -> Submenu items such as Events, Alarms, etc
          if (subElement.submenu) {
            subElement.submenu.forEach(subSubElement => {
              subSubElement.text = i18nResponse[subSubElement.i18nKey] ? i18nResponse[subSubElement.i18nKey] : subSubElement.text;
            });
          }
        });
      }
    });
    return menuList; // Return the modified original array
  }

  useEffect(() => {
    let isActive = true;

    const loadMenu = async () => {
      // set ui convergence sidebar
      if (isUIconvergence) {
        if (globalFilter?.menuState?.data === null) {
          let vManageMenuItems = getLocalStorageFlag("cl-navigation");

          const clientData = await apiService.getvManageClientData();
          if (!isActive) return;

          vManageMenuItems = vManageMenuItems && JSON.parse(vManageMenuItems) ?
            JSON.parse(vManageMenuItems) :
            null;

          if (mount.mounted === true) {
            mount.mounted = false;
            const normalizeLanguage = (localeValue) =>
              localeValue ? localeValue.split(/[-_]/)[0].toLowerCase() : "";
            const apiLocale = clientData?.data?.data?.locale || clientData?.data?.data?.language;
            const apiLanguage = normalizeLanguage(apiLocale);
            const browserLanguage = normalizeLanguage(navigator.language);
            const fallbackLanguage = normalizeLanguage(i18n?.language || localStorage.getItem("userLanguage")) || browserLanguage || "en";

            // API locale takes priority, except when API reports English and browser is non-English.
            const prioritizedLanguage = apiLanguage
              ? (apiLanguage === "en" && browserLanguage && browserLanguage !== "en" ? browserLanguage : apiLanguage)
              : fallbackLanguage;

            const LOCALE_FILE_MAP = {
              fr: "/app/json/i18n/fr_CA.json",
              ja: "/app/json/i18n/ja_JP.json",
              en: "/app/json/i18n/en_US.json"
            };
            const uiLanguage = LOCALE_FILE_MAP[prioritizedLanguage] ? prioritizedLanguage : "en";
            const i18nJsonFilePath = LOCALE_FILE_MAP[uiLanguage];

            // Fetch the i18n JSON file
            const response = await apiService.getI18nJsonFile(i18nJsonFilePath);
            if (!isActive) return;

            // The response data contains the parsed JSON object
            const i18nResponse = response?.data
              ? { ...response?.data, ...{ "navigation.analytics.bandwidthForecast": "Bandwidth Forecast" }, ...{ "navigation.analytics.anomalyDetection": "Anomaly Detection" } }
              : {};

            let extention = '';
            //mode will be provider or tenant or multiTenant-provider or multiTenant-tenant

            //update navigation API with extention
            if (mode.current === 'multiTenant-provider') {
              extention = 'type=provider-tenant';
            } else if (mode.current === 'multiTenant-tenant') {
              extention = 'type=tenant';
            }

            //Only call navigation api when cl-navigation cookie does not exist
            let menuResponse = "";

            if (vManageMenuItems === null) {
              menuResponse = await apiService.getNavigationPanelList(extention);
              if (!isActive) return;
            }
            //case 1 api failed and no cookie exists then set menuState to empty
            //case 2 api failed set menuState to empty
            //case 3 api success and no cookie exists
            if ((menuResponse?.errorObject instanceof Object) === true) {
              setMenuState({ data: null });
              setVManageNavList([]);
            } else {
              //case 5 api success and its dynamic (type === convergence)
              //case 6 api success and its static, then use cookie data vManageMenuItems or api response data
              const navData = menuResponse?.data?.data ? menuResponse?.data?.data : vManageMenuItems;
              //const navData = foo.data;

              if (!Array.isArray(navData) || navData.length === 0) {
                setMenuState({ data: null });
                setVManageNavList([]);
                return;
              }

              //check if the response has dynamic menu (type === convergence) or static menu
              //navData[0] is for Monitor menu which is dynamic
              if (Array.isArray(navData[0]?.submenu)) {
                navData[0].submenu.forEach(item => {
                  if (item.type === "convergence") {
                    menuTypeConvergence.current = true;
                  }
                });
              }
              //remove cl-monitor-tabs from local storage if menuTypeConvergence is true
              //API response will always have the latest menu items
              if (menuTypeConvergence.current === true) {
                localStorage.removeItem("cl-monitor-tabs");
              }

              if (menuTypeConvergence.current === true || vManageMenuItems === null) {
                //Use i18nResponse to update menu labels since i18nResponse has the latest translations for labels
                const i18nMenu = updateMenuLabels(navData, i18nResponse);
                const filteredMenu = await filterSystem360Menu(i18nMenu);
                if (!isActive) return;

                //save cl-navigation cookie in the local storage
                setLocalStorageFlag("cl-navigation", JSON.stringify(filteredMenu));
                setVManageNavList(filteredMenu);
                setMenuState({ data: filteredMenu });
              } else {

                //Use i18nResponse to update menu labels since i18nResponse has the latest translations for labels
                const i18nMenu = updateMenuLabels(vManageMenuItems, i18nResponse);
                const filteredMenu = await filterSystem360Menu(i18nMenu);
                if (!isActive) return;

                setVManageNavList(filteredMenu)
                setMenuState({ data: filteredMenu });
              }
            }
          }
        } else if (globalFilter.menuState?.data) {
          setVManageNavList(globalFilter.menuState?.data);
        } else {
          setVManageNavList([]);
        }
      } else setMenuItems(getMenuItems(globalFilter.features));

      const prodBanner = document.querySelector(".notification-banner");
      const shellMenu = document.querySelector(".shell-menu");

      if (prodBanner && shellMenu) {

        // Get the dynamic height of the notification banner
        const prodBannerHeight = prodBanner.offsetHeight;

        // Check if the notification banner is visible
        const prodBannerVisible = prodBanner && getComputedStyle(prodBanner).display !== "none";

        // Update the top value of shell-menu based on notifcation banner visibility
        shellMenu.style.top = prodBannerVisible ? `${prodBannerHeight + 10}px` : "2px";
      }
    };

    loadMenu();

    return () => {
      isActive = false;
    };
  }, []);

  const redirectToAccount = () => navigate(routeURLs.accounts);

  const handleShellExpand = (event) => {
    event.preventDefault();
    event.stopPropagation();
    Promise.resolve().then(() => {
      const shellElement = document.getElementById("docs__shell-nav");
      if (shellElement) {
        shellElement.setAttribute("collapsed", "");
      }
    });
  };

  return (
    <div className={css.megaMenu}>
      <div className="shell-menu">
        {vManageURI && isUIconvergence ? (
          <HbrShellNav
            id="docs__shell-nav"
            collapsed
            disabled
            disableCollapseTooltip
            showCollapseButton={false}
            noResponsive
            onHbr-expand={handleShellExpand}
          >
            {tenantId && <MultiTenantMenu tenantId={tenantId} />}
            {vManageNavList.map((data, index) => (
              <Menu navItem={data} key={index} collapsed setAltoProps={setAltoProps} />
            ))}
          </HbrShellNav>
        ) : (
          <HbrShellNav
            id="docs__shell-nav"
            collapsed
            disableCollapseTooltip
            showCollapseButton={false}
            noResponsive
            onHbr-expand={handleShellExpand}
          >
            {vManageURI !== undefined && vManageURI.includes("/") ? (
              <div className="static-menu">
                {globalFilter.selectedOverlayName}
              </div>
            ) : (
              <HbrNavItem switcher collapsed value="fabric-switcher" className="nav-item-section">
                <HbrIcon
                  slot="prefix"
                  name="nav-organization-switcher"
                  size="32px"
                />
                <div slot="content-header">{tGenerics('megaMenuFabricLabel')}</div>
                <div slot="content">{globalFilter.selectedOverlayName}</div>
                <div slot="drawerHeader">{tGenerics('megaMenuFabricsLabel')}</div>
                <div slot="menu" className="sub-menu">
                  <HbrMenu shellNav className="menu-item">
                    <HbrMenuItem
                      shellNav
                      value="provider"
                      onClick={redirectToAccount}
                    >
                      {tGenerics('megaMenuViewFabricLabel')}
                    </HbrMenuItem>
                  </HbrMenu>
                </div>
              </HbrNavItem>
            )}
            <HbrMenuDivider className="nav-divider" collapsed />
            {menuItems.map(item => item.isVManageMenuItem === true ? (
              <ManagerTab
                key={item.text}
                navItem={item}
                redirectUrl={vManageURI}
              />
            ) : (<Menu key={item.text} navItem={item} />))}
          </HbrShellNav>
        )}
      </div>
    </div>
  );
};

MegaMenu.propTypes = {
  globalFilter: PropTypes.object.isRequired,
  setMenuState: PropTypes.func.isRequired,
  setAltoProps: PropTypes.func.isRequired
};

export default AppHOC(MegaMenu);
